import { useState, useEffect } from "react";
import React from "react";
import Navbar from "./Navbar";
import { AcademicCapIcon, CurrencyPoundIcon } from "@heroicons/react/20/solid";


export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [allTickets, setAllTickets] = useState([]);
  const [reply, setReply] = useState("");
  const [expandedRows, setExpandedRows] = useState({}); // For expanded AIAnswer in table
  const [error, setError] = useState(null);
  const [sarsLoading, setSarsLoading] = useState({});

  const getTicketKey = (ticket) => String(ticket?.id ?? ticket?._id ?? "");

  const getSiteKey = (ticket) => {
    const site = String(ticket?.Site ?? "").trim().toLowerCase();
    return site === "avenor" ? "avenor" : "insightos";
  };

  const getSiteLabel = (ticket) => (getSiteKey(ticket) === "avenor" ? "Avenor" : "InsightOS");

  const fetchTicketsData = async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      setError(null);
      const response = await fetch("https://api.insightos.co.uk/admin/tickets", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();

      const allTicketsData = Array.isArray(data.allTickets) ? data.allTickets : [];
      const ticketsData = Array.isArray(data.data) ? data.data : [];

      setAllTickets(allTicketsData);
      setTickets(ticketsData);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError("Failed to fetch tickets. Please try again later.");
      setAllTickets([]);
      setTickets([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketsData(true);

    const interval = setInterval(() => {
      fetchTicketsData(false);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  async function postReply(ticketId, replyText) {
    try {
      const response = await fetch("https://api.insightos.co.uk/admin/tickets/reply", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ticketId,
          reply: replyText,
        }),
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return await response.json();
    } catch (error) {
      console.error("Error replying to ticket:", error);
      throw error;
    }
  }

  function getRowBg(ticket) {
    if (ticket.Escalate === 1) return "bg-indigo-100";
    if (ticket.Escalate === 2) return "bg-emerald-100";
    return "";
  }

  function renderStars(score) {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <svg
          key={i}
          className={`inline w-5 h-5 ${i <= score ? (score === 5 ? "text-yellow-400" : "text-indigo-400") : "text-gray-200"}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <polygon points="10 15 4 18 5.5 11.5 1 7.5 7 7 10 1 13 7 19 7.5 14.5 11.5 16 18" />
        </svg>
      );
    }
    return (
      <span className={score === 5 ? "bg-transparent rounded px-2 py-1" : ""}>
        {stars}
      </span>
    );
  }

  if (loading) {
    return <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
    </div>;
  }
  
  if (error) {
    return (
      <>
        <Navbar />
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-4 my-4" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      </>
    );
  }

  const overallTickets = Array.isArray(allTickets) ? allTickets : [];
  const openTickets = tickets.filter(t => t.SARS !== 1);
  const openTicketsBySite = {
    avenor: openTickets.filter(t => getSiteKey(t) === "avenor"),
    insightos: openTickets.filter(t => getSiteKey(t) === "insightos"),
  };
  const allTicketsBySite = {
    avenor: overallTickets.filter(t => getSiteKey(t) === "avenor"),
    insightos: overallTickets.filter(t => getSiteKey(t) === "insightos"),
  };
  const selectedOpenTicket =
    openTickets.find(ticket => getTicketKey(ticket) === String(selectedTicketId)) || null;
  const sarsTickets = overallTickets.filter(t => t.SARS === 1);
  const ticketsResolved = overallTickets.filter(t => t.Escalate === 2).length;
  const ticketsOutstanding = overallTickets.filter(t => t.Escalate !== 2).length;

  const renderSiteBadge = (ticket) => {
    const siteKey = getSiteKey(ticket);
    const label = getSiteLabel(ticket);
    const className = siteKey === "avenor"
      ? "bg-amber-100 text-amber-800"
      : "bg-sky-100 text-sky-800";

    return (
      <span className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${className}`}>
        {label}
      </span>
    );
  };

  const renderOpenTicketGroup = (siteKey, title) => {
    const siteTickets = openTicketsBySite[siteKey];

    return (
      <div className="rounded-2xl border border-indigo-100 bg-white/70 shadow-sm overflow-hidden">
        <div className={`px-4 md:px-6 py-3 border-b ${siteKey === "avenor" ? "border-amber-100 bg-amber-50" : "border-sky-100 bg-sky-50"}`}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className={`text-sm font-semibold ${siteKey === "avenor" ? "text-amber-800" : "text-sky-800"}`}>
                {title}
              </h2>
              <p className="text-xs text-slate-500">
                {siteKey === "avenor"
                  ? "Tickets raised from the Avenor site."
                  : "Tickets raised from the InsightOS site."}
              </p>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
              {siteTickets.length}
            </span>
          </div>
        </div>
        {siteTickets.length === 0 ? (
          <div className="p-4 text-sm text-slate-400">No tickets for this site.</div>
        ) : (
          <ul className="list-none m-0 p-0">
            {siteTickets.map((ticket) => {
              const ticketKey = getTicketKey(ticket);
              const isSelected = String(selectedTicketId) === ticketKey;

              return (
                <li
                  key={ticketKey}
                  onClick={() => setSelectedTicketId(ticketKey)}
                  className={`cursor-pointer px-4 md:px-6 py-4 border-b border-indigo-100 transition-colors duration-150 ${
                    isSelected
                      ? "bg-indigo-100 border-l-4 border-indigo-600"
                      : "hover:bg-indigo-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold mb-1 text-indigo-700">{ticket.Question}</div>
                      <div className="text-xs text-indigo-400">{renderSiteBadge(ticket)}</div>
                    </div>
                    <span className="ml-2 mt-0.5">
                      {ticket.Billing ? (
                        <CurrencyPoundIcon className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <AcademicCapIcon className="w-5 h-5 text-indigo-500" />
                      )}
                    </span>
                  </div>
                  <div className="text-xs text-indigo-400">
                    {new Date(ticket.CreatedAt).toLocaleString()}
                  </div>
                  <div className="text-xs text-indigo-400">
                    {ticket.CompanyName} &mdash; {ticket.StaffName}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const renderAllTicketsTable = (siteKey, title, siteTickets) => {
    const tableTheme = siteKey === "avenor"
      ? {
          wrapper: "border-amber-100 bg-amber-50/40",
          header: "bg-amber-100 text-amber-900",
          rowHighlight: "bg-amber-50",
        }
      : {
          wrapper: "border-sky-100 bg-sky-50/40",
          header: "bg-sky-100 text-sky-900",
          rowHighlight: "bg-sky-50",
        };

    return (
      <section className={`mt-8 overflow-hidden rounded-2xl border ${tableTheme.wrapper} bg-white shadow-sm`}>
        <div className={`px-4 sm:px-6 lg:px-8 py-4 border-b ${tableTheme.wrapper}`}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{title}</h2>
              <p className="mt-1 text-sm text-slate-600">
                {siteKey === "avenor"
                  ? "Tickets tagged for Avenor."
                  : "Tickets tagged for InsightOS."}
              </p>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
              {siteTickets.length}
            </span>
          </div>
        </div>
        <div className="flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className={tableTheme.header}>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold sm:pl-3">
                      Question
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                      AI Answer
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                      User Score
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                      Escalate
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                      Human
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                      Company Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                      Staff Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                      Site
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                      Created At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {siteTickets.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-sm text-gray-500 text-center">
                        No tickets found for this site.
                      </td>
                    </tr>
                  ) : (
                    siteTickets.map((ticket, idx) => {
                      const ticketKey = getTicketKey(ticket);
                      const isExpanded = Boolean(expandedRows[ticketKey]);
                      const aiShort =
                        ticket.AIAnswer && ticket.AIAnswer.length > 60
                          ? ticket.AIAnswer.slice(0, 60) + "..."
                          : ticket.AIAnswer || <span className="italic text-indigo-400">No AI answer available.</span>;
                      const rowClass =
                        ticket.Escalate === 0
                          ? "bg-white"
                          : ticket.Escalate === 1
                          ? tableTheme.rowHighlight
                          : "bg-emerald-50";

                      return (
                        <React.Fragment key={ticketKey || idx}>
                          <tr
                            onClick={() => setSelectedTicketId(ticketKey)}
                            className={`cursor-pointer hover:bg-yellow-50 ${String(selectedTicketId) === ticketKey ? "bg-blue-50 animate-pulse" : ""} ${rowClass}`}
                          >
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                              {ticket.Question}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              <div className="flex items-center gap-2">
                                <span>{aiShort}</span>
                                {ticket.AIAnswer && ticket.AIAnswer.length > 60 && (
                                  <button
                                    className="text-indigo-600 underline text-xs"
                                    type="button"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setExpandedRows(r => ({ ...r, [ticketKey]: !r[ticketKey] }));
                                    }}
                                  >
                                    {isExpanded ? "Hide" : "Expand"}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              {renderStars(ticket.UserScore)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              {ticket.Escalate === 2
                                ? "Resolved"
                                : ticket.Escalate === 1
                                ? "Yes"
                                : "No"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{ticket.Human}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{ticket.CompanyName}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{ticket.StaffName}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{renderSiteBadge(ticket)}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{new Date(ticket.CreatedAt).toLocaleString()}</td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={9} className="bg-indigo-50 px-6 py-4 text-indigo-800 border-t border-indigo-100">
                                <div>
                                  <span className="font-semibold">AI Answer:</span>
                                  <div className="mt-1">{ticket.AIAnswer}</div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Navbar />
      <div className="px-4 sm:px-6 lg:px-8 mt-6">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-sm">
          <div className="overflow-hidden rounded-lg bg-indigo-50 border border-indigo-200 px-4 py-4">
            <dt className="text-xs font-medium text-indigo-500 truncate">Tickets Outstanding</dt>
            <dd className="mt-1 text-2xl font-bold text-indigo-700">{ticketsOutstanding}</dd>
          </div>
          <div className="overflow-hidden rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-4">
            <dt className="text-xs font-medium text-emerald-600 truncate">Tickets Resolved</dt>
            <dd className="mt-1 text-2xl font-bold text-emerald-700">{ticketsResolved}</dd>
          </div>
        </dl>
      </div>

      <div className="flex flex-col md:flex-row border border-indigo-200 rounded-xl overflow-hidden shadow-lg m-4 md:m-8 bg-white min-h-[60vh]">
        <div className="w-full md:w-[35%] border-b md:border-b-0 md:border-r border-indigo-100 overflow-y-auto bg-gradient-to-b from-indigo-50 to-white max-h-72 md:max-h-none space-y-4 p-4 md:p-6">
          <div className="px-4 md:px-6 py-4 rounded-xl bg-indigo-600 text-white font-bold text-lg tracking-wide shadow-sm">
            My Tickets
          </div>
          {openTickets.length === 0 ? (
            <div className="p-6 text-indigo-300">No escalated tickets available.</div>
          ) : (
            <div className="space-y-4">
              {renderOpenTicketGroup("avenor", "Avenor Tickets")}
              {renderOpenTicketGroup("insightos", "InsightOS Tickets")}
            </div>
          )}
        </div>

        <div className="flex-1 p-4 md:p-10 flex flex-col justify-start bg-gradient-to-br from-white to-indigo-50 min-h-[300px]">
          {openTickets.length === 0 ? (
            <div className="text-indigo-300 flex flex-col items-center justify-center h-full min-h-[200px]">
              <svg className="w-16 h-16 mb-4 text-indigo-200" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              <span className="text-lg">No escalated tickets to respond to.</span>
              <p className="text-sm mt-2 text-indigo-200 max-w-md text-center">All customer tickets have been addressed or are not escalated. Check the tables below for all tickets.</p>
            </div>
          ) : selectedOpenTicket ? (
            <>
              <div className="mb-6">
                <div className="font-bold text-2xl text-indigo-700 flex items-center gap-2">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8s-9-3.582-9-8 4.03-8 9-8 9 3.582 9 8z"></path></svg>
                  {selectedOpenTicket.Question}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-indigo-400">
                  <span>{new Date(selectedOpenTicket.CreatedAt).toLocaleString()}</span>
                  <span>•</span>
                  <span>{selectedOpenTicket.CompanyName} &mdash; {selectedOpenTicket.StaffName}</span>
                  <span>•</span>
                  <span>{getSiteLabel(selectedOpenTicket)}</span>
                </div>
              </div>
              <AIAnswerSection ticket={selectedOpenTicket} />
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!reply) return;
                  const ticketToRemove = selectedOpenTicket;
                  try {
                    await postReply(ticketToRemove.id, reply);
                    setReply("");
                    setSelectedTicketId(null);
                    setTickets(prev => prev.filter(t => getTicketKey(t) !== getTicketKey(ticketToRemove)));
                  } catch (error) {}
                }}
                className="flex flex-col gap-3 flex-1 mt-6"
              >
                <label className="text-indigo-700 font-medium mb-1">Your Reply</label>
                <textarea
                  placeholder="Type your reply..."
                  className="resize-y min-h-[100px] p-3 text-base rounded border border-indigo-300 focus:ring-2 focus:ring-indigo-400 outline-none"
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                />
                <button
                  type="submit"
                  className="self-end bg-indigo-600 hover:bg-indigo-700 transition-colors text-white rounded px-8 py-2 font-semibold shadow"
                >
                  Submit
                </button>
              </form>
            </>
          ) : (
            <div className="text-indigo-300 flex flex-col items-center justify-center h-full min-h-[200px]">
              <svg className="w-16 h-16 mb-4 text-indigo-200" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8s-9-3.582-9-8 4.03-8 9-8 9 3.582 9 8z"></path></svg>
              <span className="text-lg">Select a ticket to view details and reply.</span>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 mt-10">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold text-gray-900">All Tickets</h1>
            <p className="mt-2 text-sm text-gray-700">
              Tickets are split by site so Avenor and InsightOS are easy to review separately.
            </p>
          </div>
        </div>

        {renderAllTicketsTable("avenor", "Avenor Tickets", allTicketsBySite.avenor)}
        {renderAllTicketsTable("insightos", "InsightOS Tickets", allTicketsBySite.insightos)}
      </div>

      {/* SARS Requests Section */}
      {(() => {
        const handleSarsSearch = async (ticket) => {
          const ticketKey = getTicketKey(ticket);
          setSarsLoading(prev => ({ ...prev, [ticketKey]: true }));
          try {
            const response = await fetch('https://api.insightos.co.uk/admin/sarsmessages', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ ticketId: ticket.id || ticket._id, companyCode: ticket.CompanyCode }),
            });
            if (!response.ok) throw new Error('Failed to fetch SARS messages');
            const data = await response.json();
            if (data?.success) {
              await fetchTicketsData(false);
            } else {
              alert(data?.message || 'Failed to update SARS request.');
            }
          } catch (err) {
            alert('Failed to update SARS request.');
          } finally {
            setSarsLoading(prev => ({ ...prev, [ticketKey]: false }));
          }
        };

        return (
          <div className="px-4 sm:px-6 lg:px-8 mt-10 mb-10">
            <div className="mb-6">
              <h1 className="text-base font-semibold text-gray-900">SARS Requests</h1>
              <p className="mt-1 text-sm text-gray-500">Subject Access Request tickets requiring attention.</p>
            </div>
            <div className="flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Question</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">SARS Email</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Company Code</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Company Name</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created At</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sarsTickets.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-3 py-6 text-sm text-gray-500 text-center">
                            No SARS requests found.
                          </td>
                        </tr>
                      )}
                      {sarsTickets.map((ticket, idx) => {
                        const ticketKey = getTicketKey(ticket);
                        return (
                          <React.Fragment key={ticketKey || idx}>
                            <tr className="even:bg-gray-50">
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">{ticket.Question}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{ticket.SARSEmail || '-'}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{ticket.CompanyCode || '-'}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{ticket.CompanyName || '-'}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{new Date(ticket.CreatedAt).toLocaleString()}</td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm font-medium">
                                {ticket.Escalate === 2 ? (
                                  <span className="text-emerald-700">Resolved</span>
                                ) : (
                                  <span className="text-indigo-700">Open</span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                {ticket.Escalate === 2 ? (
                                  <span className="text-xs text-emerald-700 font-semibold">Resolved</span>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={sarsLoading[ticketKey]}
                                    onClick={() => handleSarsSearch(ticket)}
                                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                                  >
                                    {sarsLoading[ticketKey] ? 'Searching...' : 'Search'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );

  function AIAnswerSection({ ticket }) {
    const [showAI, setShowAI] = useState(false);
    return (
      <div className="mb-4">
        <button
          className={`mb-2 px-4 py-1 rounded font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow`}
          onClick={() => setShowAI(v => !v)}
        >
          {showAI ? "Hide AI Answer" : "Show AI Answer"}
        </button>
        {showAI && (
          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded text-indigo-800 text-xs shadow-inner">
            <div className="font-semibold mb-1">AI Answer:</div>
            <div>{ticket.AIAnswer || <span className="italic text-indigo-400">No AI answer available.</span>}</div>
          </div>
        )}
      </div>
    );
  }
}